import { alpha, Box, Chip, Stack, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { StepsEntreprise } from "./Steps";

const PageShell = styled(Box)(({ theme }) => ({
  minHeight: "calc(100vh - 120px)",
  padding: theme.spacing(2, 2, 4),
  background: `radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, 0.08)}, transparent 24%), linear-gradient(180deg, #fafdff 0%, #f5f7fb 100%)`,
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(4, 4, 6),
  },
}));

const HeroCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: 24,
  background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(245,247,255,0.98) 100%)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(4),
  },
}));

const FormCard = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: 24,
  padding: theme.spacing(2),
  backgroundColor: "#ffffff",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.1)",
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(3),
  },
}));

const HighlightCard = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(2.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const HighlightItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  color: theme.palette.text.primary,
}));

const InscriptionEntreprise = () => {
  const [token, setToken] = useState("");
  const { t } = useTranslation();
  const params = useParams();

  useEffect(() => {
    setToken(params.token);
  }, [params.token]);

  const benefits = [
    t("companyOnboarding.feature1"),
    t("companyOnboarding.feature2"),
    t("companyOnboarding.feature3"),
  ];

  return (
    <PageShell sx={{ overflow: "auto" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
            gap: { xs: 3, lg: 4 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <HeroCard>
              <Chip
                label={t("companyOnboarding.badge")}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 999,
                  backgroundColor: alpha("#4f46e5", 0.12),
                  color: "#4338ca",
                  fontWeight: 700,
                 display: { xs: "none", lg: "block" }
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "0.7rem", md: "1rem", lg: "1.7rem" },
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                {t("companyOnboarding.title")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  textAlign: { xs: "center", lg: "left" },
                  maxWidth: 680,
                  display: { xs: "none", lg: "block" }
                }}
              >
                {t("companyOnboarding.subtitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: { xs: "center", lg: "left" },
                  fontWeight: 500,
                  display: { xs: "none", lg: "block" }
                }}
              >
                {t("companyOnboarding.helper")}
              </Typography>

              <HighlightCard sx={{
                display: {xs: "none", lg: "block" }
              }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 2, textAlign: { xs: "center", lg: "left" },
                 display: { xs: "none", lg: "block" } }}
              >
                {t("companyOnboarding.highlights")}
              </Typography>
              <Stack spacing={1.5}>
                {benefits.map((benefit) => (
                  <HighlightItem key={benefit}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        mt: 0.8,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {benefit}
                    </Typography>
                  </HighlightItem>
                ))}
              </Stack>
            </HighlightCard>

            <Typography
              variant="body2"
              sx={{
                textAlign: { xs: "center", lg: "left" },
                mt: -1
              }}
            >
              {t("companyOnboarding.note")}
            </Typography>
          </HeroCard>
        </Box>

        <Box sx={{ flex: 1, display: "flex" }}>
          <FormCard>{token && <StepsEntreprise token={token} />}</FormCard>
        </Box>
      </Box>
    </Box>
    </PageShell >
  );
};

export default InscriptionEntreprise;